import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  NgZone,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import {
  CompactType,
  DisplayGrid,
  Gridster,
  GridsterApi,
  GridsterConfig,
  GridsterItem,
  GridsterItemConfig,
  GridsterPush,
  GridType,
} from 'angular-gridster2';
import { clamp } from '../../model/utility';
import { layoutRows } from '../layout/dashboard-layout-engine';
import { DashboardGridOptions, DashboardWidgetInstance, GridRect } from '../layout/dashboard-layout';
import { DashboardWidgetSize } from '../widget/dashboard-widget-definition';
import { fittedRowHeightPx, heightBelowPx, scrollContainer } from './fitted-row-height';

/** Room below the last widget to drag into while customizing */
const EDIT_MODE_SPARE_ROWS = 2;
const MAX_ROWS = 500;
const DRAG_HANDLE_CLASS = 'dashboard-drag-handle';

type WidgetSizeLimits = (type: string) => DashboardWidgetSize | undefined;
type PushDirection = 'fromNorth' | 'fromSouth' | 'fromEast' | 'fromWest';

export interface DashboardWidgetPlacement {
  id: string;
  rect: GridRect;
}

/** Every rendered widget where gridster left it, and the keyboard change that caused it, if any */
export interface DashboardGridArrangement {
  placements: DashboardWidgetPlacement[];
  keyboard?: { kind: 'moveBy' | 'resizeBy'; id: string };
}

interface DashboardGridItemContext {
  $implicit: DashboardWidgetInstance;
}

/** The content of each canvas item: `<ng-template dashboardGridItem let-widget>` */
@Directive({ selector: 'ng-template[dashboardGridItem]' })
export class DashboardGridItemDirective {
  readonly template = inject<TemplateRef<DashboardGridItemContext>>(TemplateRef);

  static ngTemplateContextGuard(
    _directive: DashboardGridItemDirective,
    _context: unknown,
  ): _context is DashboardGridItemContext {
    return true;
  }
}

interface WidgetGridsterItem extends GridsterItemConfig {
  widgetId: string;
}

interface CanvasItem {
  widget: DashboardWidgetInstance;
  gridster: WidgetGridsterItem;
}

/**
 * Renders widgets on the grid. Gridster places them: it pushes colliding widgets and compacts upward while one is
 * dragged, resized or moved with the keyboard. Once gridster has settled, the canvas reports where every widget is.
 */
@Component({
  selector: 'app-dashboard-grid-canvas',
  templateUrl: './dashboard-grid-canvas.component.html',
  styleUrls: ['./dashboard-grid-canvas.component.scss'],
  imports: [Gridster, GridsterItem, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardGridCanvasComponent {
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly widgets = input.required<readonly DashboardWidgetInstance[]>();
  readonly sizeLimits = input.required<WidgetSizeLimits>();
  readonly editing = input(false);
  readonly grid = input.required<DashboardGridOptions>();
  readonly stacked = input(false);

  readonly arranged = output<DashboardGridArrangement>();

  protected readonly itemTemplate = contentChild.required(DashboardGridItemDirective);

  protected readonly items = linkedSignal<
    { widgets: readonly DashboardWidgetInstance[]; sizeLimits: WidgetSizeLimits },
    CanvasItem[]
  >({
    source: () => ({ widgets: this.widgets(), sizeLimits: this.sizeLimits() }),
    computation: ({ widgets, sizeLimits }, previous) => {
      const reusable = new Map(previous?.value.map((item) => [item.widget.id, item.gridster]));
      return widgets.map((widget) => {
        const next = toGridsterItem(widget, sizeLimits(widget.type));
        const current = reusable.get(widget.id);
        return { widget, gridster: current && sameGridsterItem(current, next) ? current : next };
      });
    },
  });

  /** Null until measured */
  private readonly availableHeightPx = signal<number | null>(null);

  private readonly rows = computed(() => layoutRows(this.widgets()) + (this.editing() ? EDIT_MODE_SPARE_ROWS : 0));

  /** Stacked widgets scroll anyway; on the grid, rows shrink so the canvas fits the viewport */
  private readonly rowHeightPx = computed(() =>
    this.stacked() ? this.grid().rowHeightPx : fittedRowHeightPx(this.rows(), this.availableHeightPx(), this.grid()),
  );

  protected readonly heightPx = computed(() =>
    rowsToPx(this.rows(), { ...this.grid(), rowHeightPx: this.rowHeightPx() }),
  );

  protected readonly options = computed<GridsterConfig>(() => {
    const { columns, gapPx } = this.grid();
    const rowHeightPx = this.rowHeightPx();
    const editing = this.editing();
    const stacked = this.stacked();
    return {
      gridType: GridType.VerticalFixed,
      fixedRowHeight: rowHeightPx,
      margin: gapPx,
      outerMargin: false,
      minCols: columns,
      maxCols: columns,
      minRows: 1,
      maxRows: MAX_ROWS,
      maxItemCols: columns,
      maxItemRows: MAX_ROWS,
      maxItemArea: columns * MAX_ROWS,
      addEmptyRowsCount: editing ? EDIT_MODE_SPARE_ROWS : 0,
      compactType: CompactType.CompactUp,
      pushItems: true,
      swap: false,
      draggable: { enabled: editing, ignoreContent: true, dragHandleClass: DRAG_HANDLE_CLASS },
      resizable: { enabled: editing },
      itemChangeCallback: () => this.scheduleReport(),
      // The `stacked` input owns mobile layout; gridster stacks in place so widgets aren't rebuilt when it flips
      mobileBreakpoint: stacked ? Number.MAX_SAFE_INTEGER : 0,
      keepFixedHeightInMobile: true,
      displayGrid: DisplayGrid.OnDragAndResize,
      // The page scrolls, not the grid; gridster's own edge auto-scroll drags items away from the cursor
      disableScrollVertical: true,
      disableScrollHorizontal: true,
      disableWarnings: true,
      initCallback: (_gridster, api) => (this.gridsterApi = api),
      destroyCallback: () => (this.gridsterApi = undefined),
    };
  });

  private gridsterApi?: GridsterApi;
  private reportTimer?: ReturnType<typeof setTimeout>;
  private pendingKeyboard?: DashboardGridArrangement['keyboard'];

  constructor() {
    // Changes that don't come from gridster (adds, removes) need a gridster layout pass to render
    effect(() => {
      this.widgets();
      if (this.editing() && !this.stacked()) {
        afterNextRender(() => this.gridsterApi?.calculateLayout(), { injector: this.injector });
      }
    });

    // Gridster only re-measures on window resize; the canvas also narrows when the Customize panel opens
    const resizes = new ResizeObserver(() => this.zone.runOutsideAngular(() => this.gridsterApi?.resize()));
    resizes.observe(this.host.nativeElement);

    // Width changes can rewrap the toolbar above the canvas, so the host is watched along with the viewport
    const measure = (): void => this.availableHeightPx.set(heightBelowPx(this.host.nativeElement));
    const fits = new ResizeObserver(measure);
    afterNextRender(() => {
      measure();
      fits.observe(this.host.nativeElement);
      const scroller = scrollContainer(this.host.nativeElement);
      if (scroller) {
        fits.observe(scroller);
      }

      window.addEventListener('resize', measure);
    });

    this.destroyRef.onDestroy(() => {
      resizes.disconnect();
      fits.disconnect();
      window.removeEventListener('resize', measure);
      clearTimeout(this.reportTimer);
    });
  }

  /** Moves a widget by cells the way a drag would: colliders are pushed, then gridster compacts */
  moveBy(id: string, { dx, dy }: { dx: number; dy: number }): void {
    this.keyboardChange({ kind: 'moveBy', id }, (item) => {
      item.x = Math.max(0, item.x + dx);

      if (dy > 0) {
        const others = this.items()
          .filter(({ widget }) => widget.id !== id)
          .map(({ gridster }) => gridster);
        item.y = rowBelowNextUnder(item, others) ?? item.y + dy;
        return 'fromNorth';
      }

      item.y = Math.max(0, item.y + dy);
      if (dy < 0) {
        return 'fromSouth';
      }

      return dx > 0 ? 'fromWest' : 'fromEast';
    });
  }

  /** Resizes a widget by cells from its top-left corner, within its size limits */
  resizeBy(id: string, { dw, dh }: { dw: number; dh: number }): void {
    this.keyboardChange({ kind: 'resizeBy', id }, (item) => {
      item.cols = clamp(item.cols + dw, item.minItemCols ?? 1, item.maxItemCols ?? Number.POSITIVE_INFINITY);
      item.rows = clamp(item.rows + dh, item.minItemRows ?? 1, item.maxItemRows ?? Number.POSITIVE_INFINITY);
      return dh > 0 ? 'fromNorth' : 'fromWest';
    });
  }

  /** Changes the item the way gridster's own drag does: push what it hits, commit, then let gridster compact */
  private keyboardChange(
    keyboard: NonNullable<DashboardGridArrangement['keyboard']>,
    change: (item: GridsterItemConfig) => PushDirection,
  ): void {
    const gridsterItem = this.items().find((item) => item.widget.id === keyboard.id)?.gridster;
    const component = gridsterItem && this.gridsterApi?.getItemComponent(gridsterItem);
    if (!component) {
      return;
    }

    const $item = component.$item();
    const before = { x: $item.x, y: $item.y, cols: $item.cols, rows: $item.rows };
    const direction = change($item);
    const push = new GridsterPush(component);

    if (component.gridster.checkCollision($item) && !push.pushItems(push[direction])) {
      Object.assign($item, before);
    }

    component.setSize();
    this.pendingKeyboard = keyboard;
    component.checkItemChanges($item, component.item());
    push.setPushedItems();
    push.destroy();
    this.scheduleReport();
  }

  /** Gridster commits a change in steps (the item, pushed items, then compaction); report once it has settled */
  private scheduleReport(): void {
    clearTimeout(this.reportTimer);
    this.reportTimer = setTimeout(() => {
      const keyboard = this.pendingKeyboard;
      this.pendingKeyboard = undefined;
      const placements = this.items().map(({ widget, gridster }) => ({
        id: widget.id,
        rect: fromGridsterItem(gridster),
      }));

      this.zone.run(() => this.arranged.emit({ placements, keyboard }));
    });
  }
}

function rowsToPx(rows: number, { rowHeightPx, gapPx }: DashboardGridOptions): number {
  return rows === 0 ? 0 : rows * (rowHeightPx + gapPx) - gapPx;
}

function toGridsterItem(widget: DashboardWidgetInstance, size: DashboardWidgetSize | undefined): WidgetGridsterItem {
  const item: WidgetGridsterItem = { widgetId: widget.id, x: widget.x, y: widget.y, cols: widget.w, rows: widget.h };
  if (size?.min) {
    item.minItemCols = size.min.w;
    item.minItemRows = size.min.h;
  }

  if (size?.max) {
    item.maxItemCols = size.max.w;
    item.maxItemRows = size.max.h;
  }

  return item;
}

function fromGridsterItem(item: GridsterItemConfig): GridRect {
  return { x: item.x, y: item.y, w: item.cols, h: item.rows };
}

/** Gridster mutates its item objects in place, so a reused object is compared by its current values */
function sameGridsterItem(a: WidgetGridsterItem, b: WidgetGridsterItem): boolean {
  return (
    sameRect(fromGridsterItem(a), fromGridsterItem(b)) &&
    a.minItemCols === b.minItemCols &&
    a.minItemRows === b.minItemRows &&
    a.maxItemCols === b.maxItemCols &&
    a.maxItemRows === b.maxItemRows
  );
}

function sameRect(a: GridRect, b: GridRect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * Where a widget goes to move down in a grid compacted upward: just below the nearest widget under it, which then
 * slides up into its place. Without one it is already as low as it can stay; undefined then.
 */
function rowBelowNextUnder(item: GridsterItemConfig, others: readonly GridsterItemConfig[]): number | undefined {
  const under = others.filter(
    (other) => other.x < item.x + item.cols && item.x < other.x + other.cols && other.y >= item.y + item.rows,
  );
  const next = under.reduce<GridsterItemConfig | undefined>(
    (nearest, other) => (nearest && nearest.y <= other.y ? nearest : other),
    undefined,
  );
  return next && next.y + next.rows;
}
